// Shared client-side search utility. Matches are Unicode-normalized (diacritic
// folding, punctuation-as-word-boundary), tokenized with AND semantics across
// tokens and OR semantics across keys, and ranked by a four-tier score
// (exact > starts-with > substring > acronym-only).

export interface SearchOptions<T> {
  // Fields to search. Either a key of T or an extractor fn.
  keys: (keyof T | ((item: T) => string | undefined))[];
  // Sort results by relevance. Default: true.
  rank?: boolean;
  // Cap the number of results returned. Applied after ranking.
  limit?: number;
}

const DIACRITIC_PATTERN = /\p{Diacritic}/gu;
const NON_ALPHANUMERIC_PATTERN = /[^\p{L}\p{N}\s]/gu;
const WHITESPACE_PATTERN = /\s+/g;

export const normalizeSearchText = (input: string): string =>
  input
    .normalize('NFD')
    .replace(DIACRITIC_PATTERN, '')
    .toLowerCase()
    .replace(NON_ALPHANUMERIC_PATTERN, ' ')
    .replace(WHITESPACE_PATTERN, ' ')
    .trim();

export const tokenizeQuery = (query: string): string[] =>
  normalizeSearchText(query).split(' ').filter(Boolean);

// Word-initial ("acronym") match. Both args are expected to already be
// normalized via normalizeSearchText — punctuation-as-word-boundary is baked
// into the normalization pass, so 'Cross-Channel Pacing' contributes initials
// 'ccp' rather than the 'cp' the legacy PageSearch helper produced.
export const matchesAcronym = (term: string, normalizedName: string): boolean => {
  if (term.length === 0 || normalizedName.length === 0) return false;
  const initials = normalizedName
    .split(' ')
    .map(word => word[0] ?? '')
    .join('');
  return initials.length > 0 && initials.includes(term);
};

// Score one normalized field value against one token. 0 = no match; higher is
// stronger. Tiers ported from PageSearch's scoreLocation, with the "acronym-only"
// tier promoted from an implicit +1 default to an explicit low-priority match.
const scoreToken = (normalizedValue: string, token: string): number => {
  if (normalizedValue === token) return 4;
  if (normalizedValue.startsWith(token)) return 3;
  if (normalizedValue.includes(token)) return 2;
  if (matchesAcronym(token, normalizedValue)) return 1;
  return 0;
};

// Always pushes one entry per key (an empty string for a missing/non-string value),
// so the result stays index-aligned with `keys` -- scoreItemAgainstValues' priority
// bonus reads bestKeyIndex as a position in THIS array and assumes index 0 means
// "keys[0]". Skipping absent keys entirely used to compact the array, so an item
// missing keys[0] would have keys[1]'s value land at index 0 and get keys[0]'s
// priority bonus instead of its own -- and differing array lengths across items also
// meant the bonus's own denominator (numKeys) differed between items with different
// field completeness. An empty-string entry safely scores 0 in scoreToken (every
// branch there is false/vacuous against ''), so this doesn't change which items
// match, only keeps the tie-break bonus meaningful.
const extractStringValues = <T>(item: T, keys: SearchOptions<T>['keys']): string[] =>
  keys.map(key => {
    const raw = typeof key === 'function' ? key(item) : (item[key] as unknown);
    return typeof raw === 'string' ? raw : '';
  });

export const extractNormalizedValues = <T>(item: T, keys: SearchOptions<T>['keys']): string[] =>
  extractStringValues(item, keys).map(normalizeSearchText);

// AND across tokens, OR across keys. Every token must produce a positive score
// against at least one key; per-item score sums the best-scoring key per token.
// Returns 0 for a non-match, so callers can filter on `score > 0`.
//
// Within a tier, earlier keys sort above later keys — a match on `keys[0]` beats
// an equal-tier match on `keys[1]`. This is a fractional bonus small enough that
// it never crosses a tier boundary (see priorityBonus below).
export const scoreItemAgainstValues = (normalizedValues: string[], tokens: string[]): number => {
  if (tokens.length === 0) return 0;
  const numKeys = normalizedValues.length;
  let total = 0;
  for (const token of tokens) {
    let bestTier = 0;
    let bestKeyIndex = -1;
    for (let i = 0; i < numKeys; i++) {
      const s = scoreToken(normalizedValues[i], token);
      // Strict `>` preserves the earliest key at a given tier: if a later key
      // ties on tier, we don't overwrite bestKeyIndex.
      if (s > bestTier) {
        bestTier = s;
        bestKeyIndex = i;
      }
      if (bestTier === 4) break;
    }
    if (bestTier === 0) return 0;
    // Within-tier priority bonus: keys[0] > keys[1] > … at the same tier. Strictly
    // less than 1 / 1000 per token (the numKeys in the numerator and denominator
    // roughly cancel, so this ISN'T bounded by 1 / numKeys), so even summed across
    // up to ~1000 tokens the total stays under 1 and can never promote a lower-tier
    // match above a higher-tier one.
    const priorityBonus = numKeys > 1 ? (numKeys - 1 - bestKeyIndex) / (numKeys * 1000) : 0;
    total += bestTier + priorityBonus;
  }
  return total;
};

// Exposed for PageSearch-style rank-tuning use cases where a caller already has
// a single joined haystack and wants the tier score without going through the
// full searchItems pipeline.
export const scoreMatch = (haystack: string, tokens: string[]): number =>
  scoreItemAgainstValues([normalizeSearchText(haystack)], tokens);

export const searchItems = <T>(
  items: readonly T[],
  query: string,
  { keys, rank = true, limit }: SearchOptions<T>
): T[] => {
  const tokens = tokenizeQuery(query);
  // Empty query returns the input array by reference so downstream memoization
  // at the call site (e.g. useMemo over the filtered list) sees a stable value.
  // The readonly-to-mutable widening mirrors Array.prototype.filter's signature
  // and is safe because we never mutate the input.
  if (tokens.length === 0) return items as T[];

  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const values = extractNormalizedValues(item, keys);
    const score = scoreItemAgainstValues(values, tokens);
    if (score > 0) {
      scored.push({ item, score });
    }
  }

  // PERF NOTE: sorting the full match set before applying `limit` is O(n log n)
  // even when limit ≪ n. Fine at current scale (<5k-item lists); revisit as a
  // top-K heap if we ever need to filter genuinely large catalogs here.
  if (rank) {
    scored.sort((a, b) => b.score - a.score);
  }

  const result = scored.map(({ item }) => item);
  return typeof limit === 'number' ? result.slice(0, limit) : result;
};

// Shared with useFilteredStrings so its inline-extractor signature is stable
// across renders (see the key-signature note in useFilteredList).
export const stringHaystack = (value: string): string => value;

// Convenience wrapper for the common "search a list of raw strings" case.
// Callers otherwise have to hand-thread an identity extractor as `keys`, which
// is boilerplate that obscures intent.
export const searchStrings = (
  items: readonly string[],
  query: string,
  opts: Omit<SearchOptions<string>, 'keys'> = {}
): string[] => searchItems(items, query, { ...opts, keys: [stringHaystack] });
